import React from 'react';
import { EditorContent } from '@tiptap/react';
import { Box } from '@mui/material';
import { defaultItems } from './components/SlashCommandsList';
import { useTipTapEditor } from './hooks/useTipTapEditor';
import './styles/editor.css';
import { TextMenu } from './components/menus/TextMenu';
import { LinkMenu } from './components/menus';
import { TableColumnMenu, TableRowMenu } from './extensions/Table/menus';
import { ColumnsMenu } from './extensions/MultiColumn/menus';
import ImageBlockMenu from './extensions/ImageBlock/components/ImageBlockMenu';
import { ListOptionsType } from 'types/common/DropdownOption';
import './styles/index.css';

type TiptapEditorProps = {
  readOnly?: boolean;
  initialContent: string | undefined;
  multiline?: boolean;
  onChange?: (output: string) => Promise<void>;
  onFocus?: () => void;
  onBlur?: (event?: any) => void;
  onError?: (errorMessage: string) => void;
  onInsertReference?: (insertFn: (id: string) => void) => void;
  setNodeSpecificOptions?: React.Dispatch<
    React.SetStateAction<ListOptionsType[]>
  >;
};

const TiptapEditor: React.FC<TiptapEditorProps> = ({
  readOnly = false,
  initialContent = '',
  multiline = true,
  onChange,
  onFocus,
  onBlur,
  onError,
  onInsertReference,
  setNodeSpecificOptions,
}) => {
  // Configure slash commands
  const slashCommandItems = [...defaultItems].filter(
    (item) => item.title !== 'Table'
  );
  const menuContainerRef = React.useRef<HTMLDivElement>(null);

  // Helper function to convert template references to nodes
  const convertTemplateReferencesToNodes = React.useCallback(
    (jsonContent: any): any => {
      if (!jsonContent || typeof jsonContent !== 'object') {
        return jsonContent;
      }

      // Check if this is a text node that might contain template references
      if (jsonContent.type === 'text' && jsonContent.text) {
        const text = jsonContent.text;
        const referenceMatch = text.match(/^\{\{(inputs|steps)\.([^}]+)\}\}$/);

        if (referenceMatch) {
          // Convert to a reference node
          return {
            type: 'reference',
            attrs: {
              id: `${referenceMatch[1]}.${referenceMatch[2]}`,
            },
          };
        }
      }

      // Process arrays (like content arrays)
      if (Array.isArray(jsonContent)) {
        return jsonContent.map((item) =>
          convertTemplateReferencesToNodes(item)
        );
      }

      // Process object properties recursively
      const result = { ...jsonContent };
      for (const key in result) {
        if (typeof result[key] === 'object') {
          result[key] = convertTemplateReferencesToNodes(result[key]);
        }
      }

      return result;
    },
    []
  );

  const { editor, insertReferenceAtCursor, processAllReferences } =
    useTipTapEditor({
      readOnly,
      initialContent: initialContent,
      multiline,
      onChange,
      onFocus,
      onBlur,
      onError,
      slashCommandItems,
    });

  // Expose insertReferenceAtCursor to parent components
  React.useEffect(() => {
    if (onInsertReference && insertReferenceAtCursor) {
      onInsertReference(insertReferenceAtCursor);
    }
  }, [onInsertReference, insertReferenceAtCursor]);

  // Process references when editor is ready
  React.useEffect(() => {
    if (editor) {
      setTimeout(() => {
        processAllReferences(editor);
      }, 100);
    }
  }, [editor, processAllReferences]);

  if (!editor) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100%',
        '& .notion-list-item': {
          marginBottom: '0.25em',
          '& .reference-component': {
            display: 'inline-flex',
            alignItems: 'center',
            verticalAlign: 'middle',
          },
        },
        '& .ProseMirror': {
          '& ul, & ol': {
            paddingLeft: '1.5em',
            marginTop: '0.5em',
            marginBottom: '0.5em',
          },
          '& .reference-component': {
            display: 'inline-flex',
            verticalAlign: 'middle',
          },
        },
      }}
      className='notion-style-editor-container spark-content'
      data-tiptap-editor-container
    >
      {/* The editor itself */}
      <Box
        ref={menuContainerRef}
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          backgroundColor: '#fff',
        }}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <EditorContent editor={editor} />
        {/* <ContentItemMenu editor={editor} isEditable={!readOnly} /> */}
        <LinkMenu editor={editor} appendTo={menuContainerRef} />
        <TextMenu
          editor={editor}
          appendTo={menuContainerRef}
          setNodeSpecificOptions={setNodeSpecificOptions}
        />
        <ColumnsMenu editor={editor} appendTo={menuContainerRef} />
        <TableRowMenu editor={editor} appendTo={menuContainerRef} />
        <TableColumnMenu editor={editor} appendTo={menuContainerRef} />
        <ImageBlockMenu editor={editor} appendTo={menuContainerRef} />
      </Box>
    </Box>
  );
};

export default TiptapEditor;
